/**
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/encodeURIComponent#encoding_for_rfc3986
 */
export function encodeRfc3986(uriComponent: string) {
	return encodeURIComponent(uriComponent).replace(
		/[!'()*]/g,
		(c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`,
	);
}

export class UnsafeUriComponent {
	constructor(public value: string) {}
}

/**
 * Tag function for encoding URI components.
 * Useful for prevent malformed URLs, path traversals, unintended query parameter usage, etc.
 * @param {TemplateStringsArray} statics
 * @param {unknown[]} dynamics
 *
 * @example Path Traversal Prevention
 * // Take a look at the slash.
 * // If we didn't encode it, we would end up at a different URL.
 * const id = '../another-route';
 * fetch(safeUrl`https://example.com/api/route/${id}`);
 *
 * @example Query Parameter Setter Prevention
 * const id = '?force';
 * const unsafe = `https://example.com/api/user/${id}`;
 * fetch(unsafe);
 * // This is equivalent to doing `URLSearchParams.set('force', '')`
 * // Do this instead.
 * fetch(safeUrl`https://example.com/api/user/${id}`);
 *
 * @example Bypassing Encoding
 * // Sometimes, you know for sure that your URI component won't cause issues.
 * // In these cases, you can construct an `UnsafeUriComponent`.
 * const routes = {
 * 	home: 'my-home',
 * 	posts: 'all-posts',
 * };
 * const route = routes.home;
 * const id = '...'; // User provided input that will be encoded as always.
 * safeUrl`https://example.com/api/${new UnsafeUriComponent(route)}/${id}`;
 */
export const safeUrl = (
	statics: TemplateStringsArray,
	...dynamics: unknown[]
) => {
	let result = "";
	let i = 0;

	for (; i < statics.length - 1; i++) {
		result += statics[i];
		const x = dynamics[i];

		if (x instanceof UnsafeUriComponent) {
			result += String(x.value);
		} else {
			result += encodeRfc3986(String(x));
		}
	}

	result += statics[i];
	return result;
};

/**
 * Higher order function that uses {@link safeUrl} to create a new URL from a {@link baseUrl}.
 * @param {string | URL} baseUrl
 * @returns {typeof safeUrl}
 *
 * @example
 * const safeUrl = withSafeUrl('https://example.com/api/');
 * safeUrl`./posts` === 'https://example.com/api/posts'
 */
export const withSafeUrl =
	(baseUrl: string | URL): typeof safeUrl =>
	(statics, ...dynamics) =>
		new URL(safeUrl(statics, ...dynamics), baseUrl).href;
